// User roles (for lib/admin.ts; profile comes from backend /auth/me)
export type UserRole = "admin" | "superadmin" | "manager" | "tech_admin";

// Backend API URL Configuration
// Production: frontend (e.g. ssbe.sonisamaj.org) → API at ssapi.sonisamaj.org (Coolify cmdev00/ssapi).
// Override with VITE_BACKEND_URL at build time if the API is elsewhere.
// When loaded in Android WebView, Android injects scja_api_base into sessionStorage — we use that at runtime.
// In dev, use same-origin /api/v1 (proxied by Vite) unless VITE_USE_PROXY=false or VITE_BACKEND_URL is set.
const DEFAULT_BACKEND_URL =
  import.meta.env.PROD ? "https://ssapi.sonisamaj.org/api/v1" : "http://localhost:3001/api/v1";
const USE_PROXY =
  import.meta.env.DEV &&
  import.meta.env.VITE_USE_PROXY !== "false" &&
  !import.meta.env.VITE_BACKEND_URL;

/** Ensures VITE_BACKEND_URL-style values include /api/v1 so api.ts normalizePath + Nest global prefix align. */
function ensureApiV1Base(url: string): string {
  const trimmed = url.trim().replace(/\/+$/, "");
  if (!trimmed) return trimmed;
  if (trimmed.includes("/api/v1")) return trimmed;
  return `${trimmed}/api/v1`;
}

const STATIC_BACKEND_URL = USE_PROXY
  ? ""
  : ensureApiV1Base(import.meta.env.VITE_BACKEND_URL || DEFAULT_BACKEND_URL);

/** Runtime backend URL. Use this for all API calls so WebView-injected base URL is respected. */
export function getBackendUrl(): string {
  if (typeof window === "undefined") {
    return STATIC_BACKEND_URL || "http://localhost:3001/api/v1";
  }
  // Android WebView injects into both; after redirect sessionStorage may be empty so check localStorage
  const injected =
    sessionStorage.getItem("scja_api_base") || localStorage.getItem("scja_api_base");
  if (injected && injected.trim()) {
    const base = injected.trim().replace(/\/+$/, "");
    return base.includes("/api/v1") ? base : `${base}/api/v1`;
  }
  // In dev with proxy, use same-origin so Vite proxies /api/v1 to apps/api
  if (STATIC_BACKEND_URL === "" && window.location.origin) {
    return `${window.location.origin}/api/v1`;
  }
  return STATIC_BACKEND_URL;
}

if (import.meta.env.DEV) {
  console.log("🔗 Backend URL:", getBackendUrl());
  console.log("🔗 Environment:", import.meta.env.MODE);
  console.log("🔗 VITE_BACKEND_URL:", import.meta.env.VITE_BACKEND_URL || "(not set, using proxy)");
}

// Status storage URL - API endpoint to get status (runtime URL for WebView compatibility)
/** Backend may not expose /status; frontend falls back to localStorage. */
export const STATUS_STORAGE_URL = () => `${getBackendUrl()}/status`;
export const STATUS_UPDATE_API_URL = () => `${getBackendUrl()}/status`;
/** Backend public health: GET /api/v1/public/health (base already includes /api/v1) */
export const HEALTH_CHECK_URL = () => `${getBackendUrl()}/public/health`;
